/** Minimal JSON-over-HTTP helper that understands RFC 9457 problem documents from every CortexGrip API. */

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly reason: string;
  readonly fieldErrors: FieldError[];
  readonly requestId: string | null;
  readonly retryAfterSeconds: number | null;
  /** Machine-readable context the server added (e.g. which domains were taken). */
  readonly details: Record<string, unknown>;

  constructor(init: {
    status: number;
    reason: string;
    detail: string;
    fieldErrors?: FieldError[];
    requestId?: string | null;
    retryAfterSeconds?: number | null;
    details?: Record<string, unknown> | null;
  }) {
    super(init.detail);
    this.name = "ApiError";
    this.status = init.status;
    this.reason = init.reason;
    this.fieldErrors = init.fieldErrors ?? [];
    this.requestId = init.requestId ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.details = init.details ?? {};
  }

  fieldError(field: string): string | undefined {
    return this.fieldErrors.find((e) => e.field === field)?.message;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  token?: string | null;
  /** `include` sends/receives Identity's HttpOnly refresh cookie. */
  credentials?: RequestCredentials;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined | null>;
  /** `blob` returns the response's bytes (a file to preview) instead of parsing JSON. */
  responseType?: "json" | "blob";
  /** Extra request headers (e.g. `Idempotency-Key`); the ones the client sets itself win. */
  headers?: Record<string, string>;
  /**
   * Reports how many bytes of the request body have been sent. `fetch` cannot do this, so a JSON or
   * `FormData` body with this option is sent with XMLHttpRequest; errors and results are handled exactly
   * as for `fetch`.
   */
  onUploadProgress?: (sent: number, total: number) => void;
}

function withQuery(url: string, query: RequestOptions["query"]): string {
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query))
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(baseUrl: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...opts.headers, Accept: "application/json" };
  // a FormData body sets its own multipart boundary
  const form = opts.body instanceof FormData;
  if (opts.body !== undefined && !form) headers["Content-Type"] = "application/json";
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  if (opts.onUploadProgress && opts.body !== undefined)
    return uploadWithProgress<T>(withQuery(baseUrl + path, opts.query), headers, opts, opts.onUploadProgress);
  let resp: Response;
  try {
    resp = await fetch(withQuery(baseUrl + path, opts.query), {
      method: opts.method ?? "GET",
      headers,
      body: opts.body === undefined ? undefined : form ? (opts.body as FormData) : JSON.stringify(opts.body),
      credentials: opts.credentials ?? "omit",
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({
      status: 0,
      reason: "NETWORK_ERROR",
      detail: "Could not reach the server. Check your connection.",
    });
  }
  if (resp.status === 204 || resp.status === 202) {
    return undefined as T;
  }
  if (resp.ok && opts.responseType === "blob") return (await resp.blob()) as T;
  const text = await resp.text();
  const data: unknown = text ? safeJson(text) : undefined;
  if (!resp.ok) throw problemFrom(resp.status, resp.statusText, data, (name) => resp.headers.get(name));
  return data as T;
}

/** The ApiError for a failed response, read from its RFC 9457 problem document (if it sent one). */
function problemFrom(
  status: number,
  statusText: string,
  data: unknown,
  header: (name: string) => string | null,
): ApiError {
  const p = (data ?? {}) as Record<string, unknown>;
  const retry = header("Retry-After");
  return new ApiError({
    status,
    reason: typeof p.reason === "string" ? p.reason : `HTTP_${status}`,
    detail: typeof p.detail === "string" ? p.detail : statusText || "Request failed",
    fieldErrors: Array.isArray(p.errors) ? (p.errors as FieldError[]) : [],
    requestId: typeof p.request_id === "string" ? p.request_id : header("X-Request-ID"),
    retryAfterSeconds: retry && /^\d+$/.test(retry) ? Number(retry) : null,
    details: p.details && typeof p.details === "object" ? (p.details as Record<string, unknown>) : null,
  });
}

/** `request` for a JSON body that reports upload progress (see `RequestOptions.onUploadProgress`). */
function uploadWithProgress<T>(
  url: string,
  headers: Record<string, string>,
  opts: RequestOptions,
  onProgress: (sent: number, total: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const done = () => opts.signal?.removeEventListener("abort", abort);
    xhr.open(opts.method ?? "POST", url);
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    xhr.withCredentials = opts.credentials === "include";
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      done();
      if (xhr.status === 204 || xhr.status === 202) return resolve(undefined as T);
      const text = xhr.responseText;
      const data: unknown = text ? safeJson(text) : undefined;
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data as T);
      reject(problemFrom(xhr.status, xhr.statusText, data, (name) => xhr.getResponseHeader(name)));
    };
    xhr.onerror = () => {
      done();
      reject(
        new ApiError({
          status: 0,
          reason: "NETWORK_ERROR",
          detail: "Could not reach the server. Check your connection.",
        }),
      );
    };
    xhr.onabort = () => {
      done();
      reject(new DOMException("The request was cancelled", "AbortError"));
    };
    if (opts.signal?.aborted) return xhr.abort();
    opts.signal?.addEventListener("abort", abort, { once: true });
    xhr.send(opts.body instanceof FormData ? opts.body : JSON.stringify(opts.body));
  });
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** A human-readable message for any thrown value. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
